import React, { useState } from "react";
import { PrimaryButton, SecondaryButton } from "./Buttons.jsx";
import ModalBasic from "./ModalBasic.jsx";
import { FaShield, FaFingerprint, FaKey } from "react-icons/fa6";

function MFAMethodSelectionModal({ isOpen, onClose, onMethodSelected, offer = [], loading = false, error = null }) {
  const [selectedMethod, setSelectedMethod] = useState(null);

  const handleMethodSelect = (method) => {
    setSelectedMethod(method);
    if (onMethodSelected) {
      onMethodSelected(method);
    }
  };

  const handleClose = () => {
    setSelectedMethod(null);
    onClose();
  };

  const getMethodInfo = (method) => {
    switch (method) {
      case "totp":
        return {
          name: "Authenticator App",
          description: "Enter the 6-digit code from your authenticator app",
          icon: <FaShield className="w-6 h-6 text-blue-600 dark:text-blue-400" />,
          color: "blue"
        };
      case "passkey":
        return {
          name: "Passkey",
          description: "Use your device's biometric authentication",
          icon: <FaFingerprint className="w-6 h-6 text-green-600 dark:text-green-400" />,
          color: "green"
        };
      case "backup":
        return {
          name: "Backup Code",
          description: "Enter one of your backup codes",
          icon: <FaKey className="w-6 h-6 text-purple-600 dark:text-purple-400" />,
          color: "purple"
        };
      default:
        return {
          name: method,
          description: `Use ${method} for authentication`,
          icon: <FaShieldAlt className="w-6 h-6 text-gray-600 dark:text-gray-400" />,
          color: "gray"
        };
    }
  };

  const getColorClasses = (color) => {
    switch (color) {
      case "blue":
        return "bg-blue-100 dark:bg-blue-900 border-blue-200 dark:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-800/50";
      case "green":
        return "bg-green-100 dark:bg-green-900 border-green-200 dark:border-green-800 hover:bg-green-50 dark:hover:bg-green-800/50";
      case "purple":
        return "bg-purple-100 dark:bg-purple-900 border-purple-200 dark:border-purple-800 hover:bg-purple-50 dark:hover:bg-purple-800/50";
      default:
        return "bg-gray-100 dark:bg-gray-900 border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50";
    }
  };

  return (
    <ModalBasic
      id="mfa-method-selection-modal"
      modalOpen={isOpen}
      setModalOpen={handleClose}
      width={"lg"}
    >
      <div className="p-6">
        <div className="mb-6">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/50 mx-auto mb-4">
            <FaShield className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 text-center mb-2">
            Choose Authentication Method
          </h2>
          <p className="text-slate-600 dark:text-slate-400 text-center">
            Select how you'd like to complete two-factor authentication
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-red-600 dark:text-red-400 text-center">{error}</p>
          </div>
        )}

        <div className="space-y-3 mb-6">
          {offer?.map((method, index) => {
            const methodInfo = getMethodInfo(method);
            return (
              <button
                key={index}
                onClick={() => handleMethodSelect(method)}
                disabled={loading}
                className={`w-full p-4 rounded-lg border-2 transition-all duration-200 text-left ${getColorClasses(methodInfo.color)} ${
                  loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center">
                    {methodInfo.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100">
                      {methodInfo.name}
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {methodInfo.description}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="flex justify-end space-x-3">
          <SecondaryButton onClick={handleClose} disabled={loading}>
            Cancel
          </SecondaryButton>
        </div>
      </div>
    </ModalBasic>
  );
}

export default MFAMethodSelectionModal;
